/**
 * The capability and role lists shared by User Management and the Invitations
 * panel.
 *
 * Extracted from UserAdmin.js when invites gained capability selection. Both
 * surfaces render the same set, and a capability that exists in one list but not
 * the other is a silent hole: someone gets invited without an access level that
 * the edit screen would have offered, or vice versa.
 *
 * The backend's matching allowlist is CAPABILITY_KEYS in
 * api/src/users/invite-service.js, and the email labels are CAPABILITY_LABELS in
 * api/src/users/invite-email-service.js. Adding a capability means touching all
 * three plus the `users` table.
 */

export type Capability = {
	key: string;
	label: string;
	/** Tailwind text color class. */
	color: string;
};

export const CAPABILITIES: Capability[] = [
	{ key: "can_admin", label: "Admin", color: "text-red-400" },
	{ key: "can_operations", label: "Operations", color: "text-blue-400" },
	{ key: "can_manufacturing", label: "Manufacturing", color: "text-orange-400" },
	{ key: "can_producer", label: "Producer", color: "text-purple-400" },
	{ key: "can_sales", label: "Sales", color: "text-green-400" },
	{ key: "can_ai", label: "AI Assistant", color: "text-yellow-400" },
	{ key: "can_analyst", label: "Analyst", color: "text-cyan-400" },
	{ key: "can_pilot", label: "Pilot", color: "text-sky-400" },
	{ key: "can_manager", label: "Manager", color: "text-emerald-400" },
	{ key: "can_procurement", label: "Procurement", color: "text-pink-400" },
	{ key: "can_swarm", label: "Swarm", color: "text-indigo-400" },
	// Financial gates cost/price visibility. Unlike the others it is NOT
	// admin-bypassed — admins must be granted it explicitly to see prices.
	{ key: "can_financial", label: "Financial", color: "text-amber-400" },
	{ key: "can_rnd", label: "R&D pulls", color: "text-teal-400" },
	{ key: "can_connect_bot", label: "Connect a bot", color: "text-rose-400" },
	{ key: "can_animator", label: "Creative Studio (Spark)", color: "text-fuchsia-400" },
];

export const ROLES = [
	{ value: "staff", label: "Staff" },
	{ value: "admin", label: "Admin" },
	{ value: "operations", label: "Operations" },
	{ value: "manufacturing", label: "Manufacturing" },
	{ value: "producer", label: "Producer" },
	{ value: "sales", label: "Sales" },
	{ value: "procurement", label: "Procurement" },
];
